import React, { useState, useEffect, useRef } from "react";
import { useLaunchParams } from "@telegram-apps/sdk-react";
import { DesktopLayout } from "@/layouts";
import { useStats } from "@/hooks/useStats";
import { useInfiniteClosedPositions } from "@/hooks/usePositions";
import {
  Loader2,
  CheckCircle2,
  XCircle,
  BarChart3,
  TrendingDown,
  TrendingUp,
} from "lucide-react";
import { CumulativePnlChart } from "@/components/CumulativePnlChart";
import { PositionTile } from "@/components/PositionTile";
import { PositionDetailDrawerDesktop } from "@/pages/positions/drawers";
import {
  ExchangeFilterSelect,
  ExchangeFilterOption,
} from "@/components/ExchangeFilterSelect";
import { ClosedPositionData } from "@crypto-trading/common/browser";

const POSITIONS_PER_PAGE = 20;

export const StatisticsPageDesktop: React.FC = () => {
  const launchParams = useLaunchParams();
  const userId = launchParams.initData?.user?.id?.toString() || "";

  const { data: stats, isLoading, error } = useStats(userId, true);

  // Closed positions state
  const [selectedPosition, setSelectedPosition] =
    useState<ClosedPositionData | null>(null);
  const [exchangeFilter, setExchangeFilter] =
    useState<ExchangeFilterOption>("all");
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Closed positions hook
  const exchangeCodeFilter =
    exchangeFilter === "all" ? undefined : exchangeFilter;
  const {
    data: closedPositionsData,
    isLoading: isLoadingClosed,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteClosedPositions(
    userId,
    POSITIONS_PER_PAGE,
    true,
    exchangeCodeFilter
  );

  const closedPositions =
    closedPositionsData?.pages.flatMap((page) => page.positions) ?? [];

  // Infinite scroll observer for closed positions
  useEffect(() => {
    if (!hasNextPage || isLoadingClosed || isFetchingNextPage) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = loadMoreRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, [hasNextPage, isLoadingClosed, isFetchingNextPage, fetchNextPage]);

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatPercentage = (value: number): string => {
    return `${value >= 0 ? "+" : ""}${value.toFixed(2)}%`;
  };

  return (
    <>
      <DesktopLayout
        pageTitle="Statistics"
        pageDescription=""
        showRightSidebar={false}
      >
        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <p className="text-text-secondary">
              Failed to load statistics. Please try again.
            </p>
          </div>
        )}

        {/* Stats Content */}
        {!isLoading && !error && stats && (
          <div className="space-y-6">
            {/* Top Stats Cards - 4 columns */}
            <div className="grid grid-cols-4 gap-4">
              {/* Total Opened */}
              <div className="bg-tertiary border border-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                    <CheckCircle2 className="w-5 h-5 text-green-500" />
                  </div>
                </div>
                <p className="text-sm text-text-secondary mb-1">Total Opened</p>
                <p className="text-2xl font-bold text-text-primary">
                  {stats.totalOpenedPositions}
                </p>
              </div>

              {/* Total Closed */}
              <div className="bg-tertiary border border-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-red-500/10 flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-red-500" />
                  </div>
                </div>
                <p className="text-sm text-text-secondary mb-1">Total Closed</p>
                <p className="text-2xl font-bold text-text-primary">
                  {stats.totalClosedPositions}
                </p>
              </div>

              {/* Position Volume */}
              <div className="bg-tertiary border border-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-blue-500" />
                  </div>
                </div>
                <p className="text-sm text-text-secondary mb-1">
                  $ Position Volume
                </p>
                <p className="text-2xl font-bold text-text-primary">
                  {formatCurrency(stats.totalUsdAmount || 0.1)}
                </p>
              </div>

              {/* Total PnL */}
              <div className="bg-tertiary border border-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      stats.totalPnl >= 0 ? "bg-green-500/10" : "bg-red-500/10"
                    }`}
                  >
                    {stats.totalPnl >= 0 ? (
                      <TrendingUp className="w-5 h-5 text-green-500" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-500" />
                    )}
                  </div>
                </div>
                <p className="text-sm text-text-secondary mb-1">Total PnL</p>
                <p
                  className={`text-2xl font-bold ${
                    stats.totalPnl >= 0 ? "text-green-500" : "text-red-500"
                  }`}
                >
                  {formatCurrency(stats.totalPnl)}
                </p>
              </div>
            </div>

            {/* Second Row: Cumulative PnL Chart + Best/Worst Position */}
            <div className="grid grid-cols-3 gap-4">
              {/* Left: Cumulative PnL Chart - Takes 2 columns */}
              {Boolean(
                stats.cumulativePnlData.length &&
                  stats.bestPosition &&
                  stats.worstPosition
              ) && (
                <div className="col-span-2 bg-tertiary border border-border rounded-xl p-6 flex flex-col">
                  <h3 className="text-base font-semibold text-text-primary mb-4">
                    Cumulative PnL (Last 30 Days)
                  </h3>
                  {/* Chart - fills remaining space */}
                  <div className="flex-1 min-h-0">
                    <CumulativePnlChart
                      cumulativePnlData={stats.cumulativePnlData}
                      positionMarkers={stats.positionMarkers}
                      className="h-full"
                    />
                  </div>
                </div>
              )}

              {/* Right: Best and Worst Position - Stacked, Takes 1 column */}
              <div className="col-span-1 flex flex-col gap-4">
                {/* Best Position */}
                {stats.bestPosition && (
                  <div className="bg-green-500/5 border border-green-500/20 rounded-xl p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingUp className="w-5 h-5 text-green-500" />
                      <h3 className="text-base font-semibold text-text-primary">
                        Best Position
                      </h3>
                    </div>

                    {/* Position Details */}
                    <div className="space-y-3 flex-1 flex flex-col justify-center">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-text-secondary">
                          Symbol:
                        </span>
                        <span className="text-sm font-semibold text-text-primary">
                          {stats.bestPosition.symbol}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-text-secondary">
                          Type:
                        </span>
                        <span className="text-sm font-medium text-text-primary">
                          {stats.bestPosition.positionType}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-text-secondary">
                          PnL:
                        </span>
                        <span className="text-sm font-semibold text-green-500">
                          {formatCurrency(stats.bestPosition.realizedPnl)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-text-secondary">
                          PnL %:
                        </span>
                        <span className="text-sm font-semibold text-green-500">
                          {formatPercentage(stats.bestPosition.pnlPercentage)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Worst Position */}
                {stats.worstPosition && (
                  <div className="bg-red-500/5 border border-red-500/20 rounded-xl p-6 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 mb-4">
                      <TrendingDown className="w-5 h-5 text-red-500" />
                      <h3 className="text-base font-semibold text-text-primary">
                        Worst Position
                      </h3>
                    </div>

                    {/* Position Details */}
                    <div className="space-y-3 flex-1 flex flex-col justify-center">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-text-secondary">
                          Symbol:
                        </span>
                        <span className="text-sm font-semibold text-text-primary">
                          {stats.worstPosition.symbol}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-text-secondary">
                          Type:
                        </span>
                        <span className="text-sm font-medium text-text-primary">
                          {stats.worstPosition.positionType}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-text-secondary">
                          PnL:
                        </span>
                        <span className="text-sm font-semibold text-red-500">
                          {formatCurrency(stats.worstPosition.realizedPnl)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-text-secondary">
                          PnL %:
                        </span>
                        <span className="text-sm font-semibold text-red-500">
                          {formatPercentage(stats.worstPosition.pnlPercentage)}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Closed Positions Section */}
            <div className="mt-8 space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-text-primary">
                  Closed Positions
                </h2>
                <ExchangeFilterSelect
                  value={exchangeFilter}
                  onChange={setExchangeFilter}
                />
              </div>

              {/* Loading State */}
              {isLoadingClosed && closedPositions.length === 0 && (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-accent" />
                </div>
              )}

              {/* Empty State */}
              {!isLoadingClosed && closedPositions.length === 0 && (
                <div className="text-center py-12">
                  <p className="text-text-secondary">
                    No closed positions found
                  </p>
                </div>
              )}

              {/* Positions Grid */}
              {closedPositions.length > 0 && (
                <div
                  className="grid gap-4 justify-center"
                  style={{
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(480px, 700px))",
                  }}
                >
                  {closedPositions.map((position) => (
                    <div key={position.id} className="w-full max-w-[700px]">
                      <PositionTile
                        position={position}
                        onClick={() => setSelectedPosition(position)}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Load More Trigger */}
              {hasNextPage && (
                <div ref={loadMoreRef} className="flex justify-center py-8">
                  {isFetchingNextPage && (
                    <Loader2 className="w-6 h-6 animate-spin text-accent" />
                  )}
                </div>
              )}

              {/* End of List */}
              {!isLoadingClosed &&
                !hasNextPage &&
                closedPositions.length > 3 && (
                  <div className="text-center py-8">
                    <p className="text-text-secondary text-sm">
                      End of closed positions
                    </p>
                  </div>
                )}
            </div>
          </div>
        )}
      </DesktopLayout>

      {/* Position Detail Drawer */}
      <PositionDetailDrawerDesktop
        userId={userId}
        position={selectedPosition}
        open={!!selectedPosition}
        onOpenChange={(open) => !open && setSelectedPosition(null)}
        onPositionUpdate={(updatedPosition) =>
          setSelectedPosition(updatedPosition as ClosedPositionData)
        }
        source="positions_page"
      />
    </>
  );
};
